"use client";

import PageHeader from "@/components/layout/PageHeader";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { get, post, put, del } from "../../utils/service";
import { parseApiError, isAdminUser } from "@/utils/finance";
import { btnPrimary, btnGhost, btnDanger, inputCls, labelCls } from "@/components/finance/Modal";
import Modal from "@/components/finance/Modal";

interface Category { id: string; name: string; slug: string; is_active: boolean; sort_order: number }

export default function ExpenseCategoriesPage() {
  const admin = isAdminUser();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<"add" | { edit: Category } | { delete: Category } | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get<{ data: { categories: Category[] } }>("/expense-categories");
      setCategories(r.data.categories);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (admin) load(); }, [admin, load]);

  if (!admin) return <Navigate to="/expenses" replace />;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(""); setFieldErrors({});
    try { await fn(); setModal(null); await load(); }
    catch (e) { const f = parseApiError(e); setError(f.message); setFieldErrors(f.fieldErrors); }
    finally { setBusy(false); }
  };

  const openAdd = () => { setName(""); setError(""); setFieldErrors({}); setModal("add"); };
  const openEdit = (c: Category) => { setName(c.name); setError(""); setFieldErrors({}); setModal({ edit: c }); };

  const submitAdd = (e: FormEvent) => { e.preventDefault(); run(() => post("/expense-categories", { name })); };
  const submitEdit = (e: FormEvent, c: Category) => { e.preventDefault(); run(() => put(`/expense-categories/${c.id}`, { name })); };
  const toggleActive = (c: Category) => run(() => put(`/expense-categories/${c.id}`, { is_active: !c.is_active }));
  const remove = (c: Category) => run(() => del(`/expense-categories/${c.id}`));

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full max-w-2xl space-y-5">
        <PageHeader title="Expense categories" description="Add, rename or retire the categories staff choose from when logging an expense">
          <Link to="/expenses" className={btnGhost}>Back to expenses</Link>
          <button className={btnPrimary} onClick={openAdd}>Add category</button>
        </PageHeader>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-6 animate-pulse space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {categories.map((c) => (
                <li key={c.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-[11px] text-gray-500">{c.slug}{!c.is_active && " · inactive"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="text-xs text-gray-600 hover:text-gray-900" onClick={() => openEdit(c)}>Rename</button>
                    <button className="text-xs text-gray-600 hover:text-gray-900" onClick={() => toggleActive(c)}>{c.is_active ? "Deactivate" : "Activate"}</button>
                    <button className="text-xs text-red-600 hover:text-red-800" onClick={() => setModal({ delete: c })}>Delete</button>
                  </div>
                </li>
              ))}
              {!categories.length && <li className="p-10 text-center text-sm text-gray-500">No categories yet.</li>}
            </ul>
          )}
        </div>
      </div>

      <Modal open={modal === "add"} onClose={() => setModal(null)} title="Add category">
        <form onSubmit={submitAdd} className="space-y-3">
          <div><label className={labelCls}>Name</label><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} autoFocus />{fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}</div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2"><button type="button" className={btnGhost} onClick={() => setModal(null)}>Cancel</button><button className={btnPrimary} disabled={busy}>{busy ? "Saving…" : "Add"}</button></div>
        </form>
      </Modal>

      <Modal open={typeof modal === "object" && modal !== null && "edit" in modal} onClose={() => setModal(null)} title="Rename category">
        {typeof modal === "object" && modal !== null && "edit" in modal && (
          <form onSubmit={(e) => submitEdit(e, modal.edit)} className="space-y-3">
            <div><label className={labelCls}>Name</label><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} autoFocus />{fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}</div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2"><button type="button" className={btnGhost} onClick={() => setModal(null)}>Cancel</button><button className={btnPrimary} disabled={busy}>{busy ? "Saving…" : "Save"}</button></div>
          </form>
        )}
      </Modal>

      <Modal open={typeof modal === "object" && modal !== null && "delete" in modal} onClose={() => setModal(null)} title="Delete category?"
        footer={typeof modal === "object" && modal !== null && "delete" in modal ? <><button className={btnGhost} onClick={() => setModal(null)}>Keep</button><button className={btnDanger} disabled={busy} onClick={() => remove(modal.delete)}>{busy ? "Deleting…" : "Delete"}</button></> : null}>
        <p className="text-xs text-gray-600">If any expense already uses this category, it will be deactivated instead of deleted so existing records keep their label.</p>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </Modal>
    </div>
  );
}
